import { FC, useEffect, useState } from 'react';
import {
  confirmSignIn,
  fetchMFAPreference,
  setUpTOTP,
  signIn,
  signOut,
  updateMFAPreference,
  verifyTOTPSetup,
} from 'aws-amplify/auth';
import { LoginForm, LoginPageFields } from '../../components/login/login-form';
import './login-page.css';
import {
  LoginSetNewPasswordFields,
  LoginNewPasswordForm,
} from '../../components/login/login-new-password-form';
import { SetupTOTPForm, SetupTOTPFormFields } from '../../components/login/setup-totp-form';
import { useNavigate } from 'react-router-dom';
import { RoutePaths } from '../../routes/main-routes';
import { TOTPForm, TOTPFormFields } from '../../components/login/totp-form';

export type LoginUserDetails = {
  step: string;
  username: string;
  totpSetupUri?: URL;
  totpSecret?: string;
};

export const LoginPage: FC = () => {
  const navigate = useNavigate();
  const [loginDetails, setLoginDetails] = useState<LoginUserDetails>({
    step: 'START',
    username: '',
    totpSetupUri: undefined,
    totpSecret: undefined,
  });
  const [error, setError] = useState<string | null>(null);

  const handleError = (error: unknown) => {
    if (error instanceof Error) {
      setError(error.message);
    } else {
      setError(String(error));
    }
  };

  const handleStepChange = async (
    nextStep:
      | Awaited<ReturnType<typeof signIn>>['nextStep']
      | Awaited<ReturnType<typeof confirmSignIn>>['nextStep'],
    data: LoginPageFields | LoginSetNewPasswordFields | SetupTOTPFormFields | TOTPFormFields,
  ) => {
    const newLoginDetails = { ...loginDetails, step: nextStep.signInStep };
    newLoginDetails.totpSecret = undefined;
    newLoginDetails.totpSetupUri = undefined;
    if (data && 'username' in data) {
      newLoginDetails.username = data.username.toLocaleLowerCase();
    }
    if (nextStep.signInStep === 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP') {
      newLoginDetails.totpSetupUri = nextStep.totpSetupDetails.getSetupUri(
        'Studykit',
        newLoginDetails.username.toLocaleLowerCase(),
      );
      newLoginDetails.totpSecret = nextStep.totpSetupDetails.sharedSecret;
    }
    // After successful login, enforce TOTP if not yet set up
    if (nextStep.signInStep === 'DONE') {
      const mfaPreference = await fetchMFAPreference();
      const totpEnabled = mfaPreference.enabled?.includes('TOTP');
      const totpPreferred = mfaPreference.preferred === 'TOTP';

      if (!totpEnabled) {
        // User has never set up TOTP — show QR setup screen
        const totpSetupDetails = await setUpTOTP();
        setError(null);
        setLoginDetails({
          ...newLoginDetails,
          step: 'TOTP_SETUP_REQUIRED',
          totpSetupUri: totpSetupDetails.getSetupUri('Studykit', newLoginDetails.username),
          totpSecret: totpSetupDetails.sharedSecret,
        });
        return;
      } else if (!totpPreferred) {
        // TOTP exists but not marked preferred — silently fix it
        await updateMFAPreference({ totp: 'PREFERRED' });
      }
    }
    setError(null);
    setLoginDetails(newLoginDetails);
  };

  const onSubmit = async (data: LoginPageFields) => {
    try {
      data.username = data.username.toLocaleLowerCase();
      const { nextStep } = await signIn(data);
      await handleStepChange(nextStep, data);
    } catch (error) {
      handleError(error);
    }
  };

  const onNewPasswordSubmit = async (data: LoginSetNewPasswordFields) => {
    try {
      const { nextStep } = await confirmSignIn({
        challengeResponse: data.newPassword,
      });
      await handleStepChange(nextStep, data);
    } catch (error) {
      handleError(error);
    }
  };

  const onSubmitSetupTOTP = async (data: SetupTOTPFormFields) => {
    try {
      const { nextStep } = await confirmSignIn({
        challengeResponse: data.code.toString(),
      });
      await handleStepChange(nextStep, data);
    } catch (error) {
      handleError(error);
    }
  };

  const onSubmitTOTPCode = async (data: TOTPFormFields) => {
    try {
      const { nextStep } = await confirmSignIn({
        challengeResponse: data.code.toString(),
      });
      await handleStepChange(nextStep, data);
    } catch (error) {
      handleError(error);
    }
  };

  // Handles post-login TOTP setup enforcement (pool MFA is OPTIONAL but we enforce per-user)
  const onSubmitRequiredTOTPSetup = async (data: SetupTOTPFormFields) => {
    try {
      await verifyTOTPSetup({ code: data.code.toString() });
      await updateMFAPreference({ totp: 'PREFERRED' });
      setError(null);
      setLoginDetails({ ...loginDetails, step: 'DONE' });
    } catch (error) {
      handleError(error);
    }
  };

  const onCancelTOTPSetup = async () => {
    await signOut();
    setError(null);
    setLoginDetails({ step: 'START', username: '', totpSetupUri: undefined, totpSecret: undefined });
  };

  useEffect(() => {
    if (loginDetails.step === 'DONE') {
      navigate(`/${RoutePaths.Studies}`, { replace: true });
    }
  }, [loginDetails.step, navigate]);

  return (
    <div className="login-container">
      <div className="login-logo philips-shield"></div>
      <div className="login-header">Studykit Login Page</div>
      {loginDetails.step === 'START' && (
        <LoginForm onSubmit={onSubmit} error={error ?? undefined} />
      )}
      {loginDetails.step === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED' && (
        <LoginNewPasswordForm
          onNewPasswordSubmit={onNewPasswordSubmit}
          error={error ?? undefined}
        />
      )}
      {loginDetails.step === 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP' && (
        <SetupTOTPForm
          onSubmit={onSubmitSetupTOTP}
          totpSetupDetails={{
            sharedSecret: loginDetails.totpSecret!,
            setupUri: loginDetails.totpSetupUri!.toString(),
          }}
          error={error ?? undefined}
        />
      )}
      {loginDetails.step === 'TOTP_SETUP_REQUIRED' && (
        <SetupTOTPForm
          onSubmit={onSubmitRequiredTOTPSetup}
          onCancel={onCancelTOTPSetup}
          totpSetupDetails={{
            sharedSecret: loginDetails.totpSecret!,
            setupUri: loginDetails.totpSetupUri!.toString(),
          }}
          error={error ?? undefined}
        />
      )}
      {loginDetails.step === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE' && (
        <TOTPForm onSubmit={onSubmitTOTPCode} error={error ?? undefined} />
      )}
    </div>
  );
};
